import { SettingsManager } from "@viamap/viamap2-common";
import { ESInterface } from "src/compentsModus/ESInterface";
import { VersionedObjectPersistence } from "src/common/managers/ESVersionedPersistance";
import type { ANNAnnotationRecord } from "./ANNTypes";

const persistenceCache: Map<string, ANNAnnotationPersistence> = new Map();
const indexCreationPromises: Map<string, Promise<void>> = new Map();

export function getAnnotationIndexName(organizationId?: string): string {
   const baseIndexName = SettingsManager.getSystemSetting("ann.projectsElasticIndex", undefined, true);
   if (!baseIndexName) {
      throw new Error("Elasticsearch index name not configured. Please set 'ann.projectsElasticIndex' in system settings.");
   }
   return organizationId ? `${baseIndexName}-${organizationId}` : baseIndexName;
}

export function getAnnotationIndexMappings() {
   return {
      mappings: {
         properties: {
            id: { type: "keyword" },
            documentId: { type: "keyword" },
            bfeNumber: { type: "keyword" },
            contextType: { type: "keyword" },
            organizationId: { type: "keyword" },
            createdAt: { type: "date" },
            createdBy: { type: "keyword" },
            createdByName: { type: "text" },
            title: { type: "text" },
            comment: { type: "text" },
            type: { type: "keyword" },
            layers: { type: "object", enabled: false },
            note: { type: "object", enabled: false },
            document: { type: "object", enabled: false },
            history: { type: "object", enabled: false },
            geometries: { type: "geo_shape" },
         },
         _meta: {
            CreatedBy: "ANNModule",
            CreatedAt: new Date().toISOString()
         }
      }
   };
}

export class ANNAnnotationPersistence extends VersionedObjectPersistence<ANNAnnotationRecord> {
   constructor(indexName: string, userId: string) {
      super(indexName, userId, getAnnotationIndexMappings().mappings, (data) => data.id);
   }

   async ensureIndexExists(): Promise<void> {
      if (indexCreationPromises.has(this.esIndexName)) {
         return indexCreationPromises.get(this.esIndexName)!;
      }
      const creationPromise = (async () => {
         const exists = await this.es.indexExists(this.esIndexName);
         if (exists) {
            indexCreationPromises.delete(this.esIndexName);
            return;
         }
         try {
            await this.es.createIndex(this.esIndexName, getAnnotationIndexMappings());
         } catch (err: any) {
            const errorMessage = err?.message || String(err);
            if (!errorMessage.includes("resource_already_exists") && !errorMessage.includes("already exists")) {
               throw err;
            }
         } finally {
            indexCreationPromises.delete(this.esIndexName);
         }
      })();
      indexCreationPromises.set(this.esIndexName, creationPromise);
      return creationPromise;
   }

   getEs(): ESInterface {
      return this.es;
   }
}

export function getAnnotationPersistence(organizationId?: string, userId?: string): ANNAnnotationPersistence {
   const indexName = getAnnotationIndexName(organizationId);
   const resolvedUserId = userId || "system";
   const cacheKey = `${indexName}::${resolvedUserId}`;
   if (!persistenceCache.has(cacheKey)) {
      persistenceCache.set(cacheKey, new ANNAnnotationPersistence(indexName, resolvedUserId));
   }
   return persistenceCache.get(cacheKey)!;
}
